import { useTranslation } from 'react-i18next';
import SEO, { breadcrumbJsonLd } from '../../components/SEO';
import { DEMO_URL } from '../../data/links';
import ContactForm, { CONTACT_EMAIL } from './ContactForm';

const CONTACT_PHONE = '+91 7303993102';
const STEP_KEYS = ['review', 'call', 'demo'] as const;

export default function ContactPage() {
  const { t } = useTranslation();

  return (
    <div className="pt-24">
      <SEO
        title="Contact Us"
        description="Get in touch with the Geofleet team. Book a demo, request pricing, or get expert support for your delivery management needs."
        jsonLd={breadcrumbJsonLd([
          { name: 'Home', url: 'https://geofleet.ai' },
          { name: 'Contact', url: 'https://geofleet.ai/contact' },
        ])}
      />
      <section className="relative overflow-hidden bg-gradient-to-b from-slate-50 to-white py-12 lg:py-20">
        <div className="absolute top-0 right-0 w-[520px] h-[320px] bg-blue-500/[0.06] rounded-full blur-3xl -translate-y-1/3 translate-x-1/4" />
        <div className="relative max-w-6xl mx-auto px-4 sm:px-6 grid lg:grid-cols-12 gap-10 lg:gap-14 items-start">
          <div className="lg:col-span-5 lg:pt-6">
            <span className="inline-flex items-center gap-2 bg-blue-50 text-blue-700 px-3 py-1.5 rounded-full text-xs font-semibold mb-5">
              <i className="ri-chat-smile-2-line" />
              {t('contact.page.eyebrow')}
            </span>
            <h1 className="text-3xl sm:text-4xl lg:text-[2.6rem] leading-tight font-bold text-gray-900 tracking-tight mb-4">
              {t('contact.page.title')}
            </h1>
            <p className="text-lg text-gray-600 mb-10">{t('contact.page.subtitle')}</p>

            <h2 className="text-xs font-semibold uppercase tracking-wider text-gray-500 mb-4">{t('contact.page.nextTitle')}</h2>
            <ol className="space-y-4 mb-10">
              {STEP_KEYS.map((k, i) => (
                <li key={k} className="flex gap-3.5">
                  <span className="flex-shrink-0 w-7 h-7 rounded-full bg-blue-600 text-white text-sm font-semibold flex items-center justify-center">
                    {i + 1}
                  </span>
                  <p className="text-[15px] text-gray-700 pt-0.5">{t(`contact.page.steps.${k}`)}</p>
                </li>
              ))}
            </ol>

            <div className="flex flex-col gap-2.5 text-[15px]">
              <a href={`mailto:${CONTACT_EMAIL}`} className="inline-flex items-center gap-2.5 text-gray-700 hover:text-blue-600">
                <i className="ri-mail-line text-lg text-gray-400" />
                {CONTACT_EMAIL}
              </a>
              <a href={`tel:${CONTACT_PHONE.replace(/\s/g, '')}`} className="inline-flex items-center gap-2.5 text-gray-700 hover:text-blue-600" dir="ltr">
                <i className="ri-phone-line text-lg text-gray-400" />
                {CONTACT_PHONE}
              </a>
              <a
                href={DEMO_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2.5 text-blue-600 font-medium hover:text-blue-700"
              >
                <i className="ri-calendar-line text-lg" />
                {t('contact.page.bookDirect')}
              </a>
            </div>
          </div>

          <div className="lg:col-span-7">
            <div className="bg-white rounded-2xl border border-gray-200 shadow-xl shadow-gray-900/[0.04] p-5 sm:p-8">
              <ContactForm />
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
